/**
 * PsicoSalud — Estado vacío con CTA.
 *
 * Reemplaza los placeholders italic de "no hay datos" por un bloque
 * centrado y amable: ícono sutil, título, descripción y (cuando tiene
 * sentido) un call-to-action para crear el primer registro. La idea es
 * que un sistema recién cargado no se sienta vacío sino listo para
 * recibir la primera captura del profesional.
 *
 *   <EmptyState
 *     icon={<IconPerson />}
 *     title="Aún no hay pacientes registrados"
 *     description="Empezá creando la primera ficha para habilitar el historial clínico y las consultas."
 *     action={{ label: 'Nuevo paciente', onClick: () => setShowForm(true) }}
 *   />
 *
 * Si el destino del CTA es una ruta interna, usar `to` en vez de
 * `onClick` para que el botón siga siendo un Link de react-router.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  to?: string;
}

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  /** Texto secundario bajo la acción principal (ej: "o importá una lista"). */
  hint?: ReactNode;
  className?: string;
  children?: ReactNode;
}

export default function EmptyState({
  icon,
  title,
  description,
  action,
  hint,
  className = '',
  children,
}: EmptyStateProps) {
  const cls = `empty-state-rich ${className}`.trim();
  return (
    <div className={cls} data-cy="empty-state">
      {icon && (
        <div className="empty-state-icon" aria-hidden="true">
          {icon}
        </div>
      )}
      <h3 className="empty-state-title">{title}</h3>
      {description && <p className="empty-state-description">{description}</p>}
      {action && (
        action.to ? (
          <Link to={action.to} className="is-button empty-state-action" data-cy="empty-state-cta">
            {action.label}
          </Link>
        ) : (
          <button
            type="button"
            onClick={action.onClick}
            className="empty-state-action"
            data-cy="empty-state-cta"
          >
            {action.label}
          </button>
        )
      )}
      {hint && <div className="empty-state-hint">{hint}</div>}
      {children}
    </div>
  );
}

// ============================================================
// Set de iconos minimalistas para empty states.
// Trazo fino, color hereda de currentColor; quien los use puede
// sobreescribir el color con la propiedad CSS correspondiente.
// ============================================================

export function IconPerson({ size = 56 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="28" cy="20" r="8" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12 44c0-8 7-14 16-14s16 6 16 14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function IconCalendar({ size = 56 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="9" y="13" width="38" height="34" rx="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 22h38" stroke="currentColor" strokeWidth="1.5" />
      <path d="M19 9v8M37 9v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="20" cy="31" r="1.5" fill="currentColor" />
      <circle cx="28" cy="31" r="1.5" fill="currentColor" />
      <circle cx="36" cy="31" r="1.5" fill="currentColor" />
    </svg>
  );
}

export function IconNotebook({ size = 56 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="11" y="8" width="30" height="40" rx="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M11 16h30M11 24h30M11 32h30M11 40h22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M44 12v32" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function IconClipboard({ size = 56 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="11" y="12" width="34" height="36" rx="3" stroke="currentColor" strokeWidth="1.5" />
      <rect x="20" y="8" width="16" height="8" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M18 26h20M18 32h20M18 38h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function IconChart({ size = 56 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 48h44" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="14" y="30" width="6" height="14" stroke="currentColor" strokeWidth="1.5" rx="1" />
      <rect x="26" y="20" width="6" height="24" stroke="currentColor" strokeWidth="1.5" rx="1" />
      <rect x="38" y="12" width="6" height="32" stroke="currentColor" strokeWidth="1.5" rx="1" />
    </svg>
  );
}

export function IconUsers({ size = 56 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="22" cy="20" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 44c0-7 6-12 13-12s13 5 13 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="38" cy="22" r="5.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M36 32c6 0 11 4 11 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function IconSearch({ size = 56 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="12" stroke="currentColor" strokeWidth="1.5" />
      <path d="M33 33l11 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
